import { useRouter } from '@tanstack/react-router';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import type { AugmentedAccount } from '@/services/auth/types';
import { useSqlImportDatabases } from '@/services/db/sql-import-database/queries';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import { useObjectTypes } from '@/services/db/object-type/queries';
import { useSegmentOptional } from '@/services/db/segment/queries';
import { SQL_DATABASE_CATALOG, sqlImportSyncTarget } from '@/features/sql-sync';
import { epochNowInSeconds } from '@/utils/time';
import { SQL_SYNC_LIST_ROUTE_PATHS } from '../hooks';
import type { SqlImportDraft } from './draft';
import { computeWizardSteps, wizardActionState } from './steps';
import { DETAILS_FORM_ID } from './components/details-step';
import { SETTINGS_FORM_ID } from './components/settings-step';
import { settingsSubmittedProperties, type SettingsFormValues } from './settings';
import { SQL_SERVER_IPS } from './database-form';
import { useWizardDatabase } from './use-database-step';
import { useQuerySteps } from './use-query-steps';
import { useSaveSqlImport, useSqlImportLeaveGuard } from './use-save-sql-import';
import { useWizardNavigation } from './use-wizard-navigation';
import { useSqlImportDraft } from './use-sql-import-draft';

const CREATE_PERMISSION = 'journeys/sql_syncs/create';
const CREATE_SEGMENT_PERMISSION = 'journeys/segments/create';

/** The existing segment an import targets, named and linkable on review; null until it resolves or when there is none. */
function useReviewSegment(workspaceId: number, segmentId: string | undefined): { id: string; name: string } | null {
  const segment = useSegmentOptional({ workspaceId, id: segmentId ?? '', enabled: segmentId != null });
  if (segment.data == null) {
    return null;
  }
  return { id: segment.data.id, name: segment.data.name };
}

/** Relationships need object types to relate to, and a plan above Builder. */
function allowsRelationships(objectTypes: readonly unknown[], account: AugmentedAccount): boolean {
  return objectTypes.length > 0 && !isBuilderPlan(account);
}

type UseCreateSyncWizardArgs = {
  workspaceId: string;
  type: SqlDatabaseType;
  /** The saved draft being resumed, from the `id` query param. */
  draftId: string | null;
  initialDraft: SqlImportDraft;
  /** The workspace identifies people by email, so the Settings step offers that identifier. */
  canIdentifyByEmail: boolean;
  /** A draft was saved for the first time; the URL should address it. */
  onDraftSaved: (id: string) => void;
};

/**
 * Everything the wizard renders from: the draft and its baseline, the step machine, the
 * footer actions, the saves, and the leave guard. The component is layout only.
 */
export function useCreateSyncWizard({
  workspaceId,
  type,
  draftId,
  initialDraft,
  canIdentifyByEmail,
  onDraftSaved,
}: UseCreateSyncWizardArgs) {
  const router = useRouter();
  const { track } = useAnalytics();
  const auth = useAuth();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(CREATE_PERMISSION);
  const canCreateSegment = hasPermission(CREATE_SEGMENT_PERMISSION);

  const { data: databases } = useSqlImportDatabases({ type, workspaceId: Number(workspaceId) });
  const { data: objectTypes } = useObjectTypes({ workspaceId: Number(workspaceId) });
  const info = SQL_DATABASE_CATALOG[type];

  const { draft, savedId, isDirty, hasUnsavedChanges, setDraftFields, resetToBaseline, markSaved } = useSqlImportDraft(
    initialDraft,
    draftId,
  );

  const withRelationships = allowsRelationships(objectTypes, auth.account);
  const syncTarget = sqlImportSyncTarget(draft, objectTypes);
  const { databaseStep, database } = useWizardDatabase({
    workspaceId: Number(workspaceId),
    type,
    databaseTypeName: info.name,
    syncTarget,
    databases,
    draft,
    setDraftFields,
  });
  const segment = useReviewSegment(Number(workspaceId), draft.segment_id);
  const listLocation = { to: SQL_SYNC_LIST_ROUTE_PATHS.migrated, params: { workspaceId, type } } as const;

  const { isSaving, saveDraft, publish } = useSaveSqlImport({
    workspaceId,
    type,
    draft,
    savedId,
    syncTarget,
    onSaved: markSaved,
    onFirstDraftSaved: onDraftSaved,
  });
  const { step, setStep, goBack, goNext } = useWizardNavigation({
    withRelationships,
    onExit: () => void router.navigate(listLocation),
    onPublish: () => void publish(),
    onEnter: (next) => querySteps.onEnterStep(next),
  });
  const querySteps = useQuerySteps({
    workspaceId: Number(workspaceId),
    type,
    databaseTypeName: info.name,
    syncTarget,
    draft,
    // The database the Database step shows as selected, so a preview cannot run against another one.
    databaseId: database?.id,
    savedId,
    objectTypes,
    setDraftFields,
    advance: () => goNext(),
  });
  useSqlImportLeaveGuard({ hasUnsavedChanges, canCreate, isSaving, saveDraft, onDiscard: resetToBaseline });

  const steps = computeWizardSteps(
    {
      draft,
      database: databaseStep.progressDatabase,
      hasSuccessfulQuery: querySteps.hasSuccessfulQuery,
      hasSuccessfulRelationshipsQuery: querySteps.hasSuccessfulRelationshipsQuery,
      withRelationships,
      nowInSeconds: epochNowInSeconds(),
    },
    step,
  );

  return {
    databaseTypeName: info.name,
    title: `Sync your ${info.name} ${info.store}`,
    exitHref: router.buildLocation(listLocation).href,
    canCreate,
    canSave: isDirty && canCreate,
    isSaving,
    saveDraftFromHeader: () => void saveDraft({ withUrl: true }),
    steps,
    step,
    selectStep: setStep,
    goBack,
    goNext,
    actions: wizardActionState(step, {
      canCreate,
      isSaving,
      detailsFormId: DETAILS_FORM_ID,
      settingsFormId: SETTINGS_FORM_ID,
      canAdvanceDatabase: databaseStep.canAdvance,
      hasSuccessfulQuery: querySteps.hasSuccessfulQuery,
      hasSuccessfulRelationshipsQuery: querySteps.hasSuccessfulRelationshipsQuery,
    }),
    draft,
    setDraftFields,
    database,
    databases,
    databaseStep,
    sqlServerIp: SQL_SERVER_IPS[auth.account.data_center],
    syncTarget,
    segment,
    onDetailsSubmit: () => {
      void track('sqlImports', 'settingsStarted', { 'Database Type': info.name });
      goNext();
    },
    objectTypes,
    canIdentifyByEmail,
    canCreateSegment,
    querySteps,
    isNewImport: savedId == null,
    onSettingsSubmit: (values: SettingsFormValues) => {
      void track('sqlImports', 'selectDatabaseStarted', settingsSubmittedProperties(values));
      goNext();
    },
  };
}

export type CreateSyncWizardState = ReturnType<typeof useCreateSyncWizard>;
